import React from 'react';
import { motion } from 'framer-motion';
import { useSwipe } from './useSwipe';
import { SwipeProps, SwipeEvents } from './Swipe.model';
import { pickDefined } from '../utils';

export type { SwipeProps };

/**
 * Swipe component
 *
 * Renders a draggable element with swipe animation.
 *
 * @example
 * ```tsx
 * <Swipe
 *   threshold={0.3}
 *   onThresholdReached={(dir) => console.log('Swiped', dir)}
 * >
 *   <div>Drag me!</div>
 * </Swipe>
 * ```
 */
export const Swipe = React.forwardRef<HTMLDivElement, SwipeProps>(
  (
    {
      children,
      className,
      id,
      as = 'div',
      motionProps: additionalMotionProps,
      threshold,
      duration,
      dragElastic,
      direction,
      enabled,
      dragConstraints,
      dragDirectionLock,
      onSwipeStart,
      onSwipeProgress,
      onSwipeUpdate,
      onSwipeEnd,
      onThresholdReached,
    },
    ref
  ) => {
    const config = pickDefined({
      threshold,
      duration,
      dragElastic,
      direction,
      enabled,
      dragConstraints,
      dragDirectionLock,
    });

    const events: SwipeEvents = {
      onSwipeStart,
      onSwipeProgress,
      onSwipeUpdate,
      onSwipeEnd,
      onThresholdReached,
    };

    const { motionProps } = useSwipe(config, events);

    const MotionComponent = motion(as);

    const finalMotionProps = {
      ...motionProps,
      ...additionalMotionProps,
      ref,
      className,
      id,
    };

    return <MotionComponent {...finalMotionProps}>{children}</MotionComponent>;
  }
);

Swipe.displayName = 'Swipe';
